import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import { Modal, Button, Icon } from 'semantic-ui-react';
import { SemanticModal } from 'flow-core-components-shared';
import {toast, ToastContainer} from "react-toastify";
import styles from './Messages.scss';
import cx from 'classnames';
import t from './translations';
import { translate } from "@flow-frontend-npm/common";
import DOMPurify from "dompurify";

const AUTO_CLOSE_TIMEOUT = 15000;
const REMOVE_ALL_TOASTS_THRESHOLD = 4;

const SEVERITIES = [
    {
        name: 'INFO',
        severity: 1,
        autoClose: AUTO_CLOSE_TIMEOUT,
        toastType: toast.TYPE.INFO,
        consoleMethod: 'info'
    },
    {
        name: 'WARNING',
        severity: 2,
        autoClose: AUTO_CLOSE_TIMEOUT,
        toastType: toast.TYPE.WARNING,
        consoleMethod: 'warn'
    },
    {
        name: 'ERROR',
        severity: 3,
        autoClose: false,
        toastType: toast.TYPE.ERROR,
        consoleMethod: 'error'
    },
    {
        name: 'FATAL',
        severity: 4,
        autoClose: false,
        toastType: toast.TYPE.ERROR,
        consoleMethod: 'error'
    }
];
const TOAST_CONTAINER_ID = "messages";

const MESSAGE_MANAGER = {
    show: messages => {
        messages.map( msg => {
            // Dummy implementation just logs to console.
            // When MessagesContainer is instantiated,
            // it replaces this with the proper implementation.
            const severity = SEVERITIES.find(s => s.name === msg.severity);
            console[severity ? severity.consoleMethod : 'log'](msg.text); // eslint-disable-line
        });
    }
};

let lastMessageID = 0;

/**
 * @param {Array} messages
 * @return {void|undefined}
 */
export const showMessages = messages => messages ? MESSAGE_MANAGER.show(messages.map(msg => {
    msg.id = ++lastMessageID;
    return msg;
})) : undefined;

export const messagePropType = PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string.isRequired,
    severity: PropTypes.oneOf(SEVERITIES.map(s => s.name)).isRequired,
    popup: PropTypes.bool.isRequired
});

/**
 * Creates new array where the first instance of the 2nd parameter is removed.
 * @param {Array} values
 *      array to remove value from
 * @param {any} toBeRemoved
 *      value to be removed once
 * @return {Array}
 *      a new array where the first instance of the 2nd parameter is removed
 */
function arrayWithout(values, toBeRemoved) {

    const index = values.indexOf(toBeRemoved);
    if (index === -1) {
        return values;
    }
    const without = [...values];
    without.splice(index, 1);
    return without;
}

/**
 * @return {*}
 *      make a guess
 */
const Messages = ({mountToastContainer = true}) => {

    const [popupMessages, setPopupMessages] = useState([]);
    const [toastIds, setToastIds] = useState([]);

    useEffect(() => {
        toast.onChange(payload => setToastIds(oldIds => {
            switch (payload.status) {
                case 'added':
                    return [...oldIds, payload.id];
                case 'removed':
                    return arrayWithout(oldIds, payload.id);
                default:
                    return oldIds;
            }
        }));
    }, []);

    /**
     * @param {Array} messages
     */
    MESSAGE_MANAGER.show = messages => {
        messages.filter(msg => !msg.popup).forEach(showToast);
        const popupMessages = messages.filter(msg => msg.popup);
        popupMessages.reverse(); // Reverse so that first message is shown on top.
        setPopupMessages(popupMessages);
    };

    /**
     * Shows message as toast.
     * @param {object} message
     */
    const showToast = (message) => {
        const severity = SEVERITIES.find(s => s.name === message.severity);
        toast( message.text, {
            type: severity.toastType || toast.TYPE.DEFAULT,
            autoClose: severity.autoClose,
            containerId: TOAST_CONTAINER_ID
        });
    };

    /**
     * Does what it says.
     */
    const dismissAllToasts = () => {
        toastIds.forEach( eachToastId => toast.dismiss(eachToastId) );
        setToastIds([]);
    };

    /**
     * @return {*}
     */
    return <>
        {
            mountToastContainer && (
                <ToastContainer
                    autoClose={15000}
                    position="bottom-right"
                    className={cx(
                        styles.ToastContainer,
                        (toastIds.length >= REMOVE_ALL_TOASTS_THRESHOLD) && styles.shiftedUp
                    )}
                />
            )
        }
        {
            <div
                className={cx(
                    styles.CloseAllActionButton,
                    (toastIds.length >= REMOVE_ALL_TOASTS_THRESHOLD) && styles.visible
                )} >
                <button onClick={dismissAllToasts}>
                    {translate(t, 'messages.close-all')} &#x1F7AA;
                </button>
            </div>
        }
        {
            popupMessages.map(msg => (
                <MessageModal
                    message={msg}
                    key={msg.id}
                />
            ))
        }
    </>;
};
Messages.propTypes = {
    mountToastContainer: PropTypes.bool

};

/**
 * @param {object} msg
 * @return {*}
 */
const renderMessage = (msg) => (
    <p
        className={cx(styles.message, styles[msg.severity])}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.text) }}
    />
);
/**
 * @return {*}
 *      the message-popup
 */
const MessageModal = ({message}) => {

    const [open, setOpen] = useState(true);

    /**
     * Closes the modal.
     */
    const handleClose = () => {
        setOpen(false);
    };

    /**
     * Renders the modal.
     * @return {boolean|*}
     */
    return (!!message &&
        <SemanticModal open={open} key={message.id}
            onClose={handleClose} className={styles.MessageModal}
        >
            <Modal.Header>{translate(t, 'messages.titles.' + message.severity)}</Modal.Header>
            <Modal.Content>{renderMessage(message)}</Modal.Content>
            <Modal.Actions>
                <Button color='green' onClick={handleClose}><Icon name='checkmark'/> OK</Button>
            </Modal.Actions>
        </SemanticModal>
    );
};

MessageModal.propTypes = {
    message: messagePropType.isRequired
};

export default Messages;
